import { CopyButton, Tooltip } from "@mantine/core";
import * as React from "react";
import type { SaleOrderLineView } from "../types";
import { formatNumberVi } from "../config/theme";

interface OrderLinesTableProps {
  lines: SaleOrderLineView[];
  height: number;
  readOnly: boolean;
  /** Bấm mã hàng để tìm serial của mã đó */
  onPickItem: (itemCode: string) => void;
  /** Đổi giá trị (khác null) thì cuộn tới dòng của mã hàng này và tô nổi một lúc */
  focusItemCode?: string | null;
}

interface Totals {
  quantity: number;
  amount: number;
  scanned: number;
  deviated: number;
}

interface CellContext {
  readOnly: boolean;
  onPickItem: (itemCode: string) => void;
}

interface Column {
  id: string;
  label: string;
  /** Bề rộng px; cột co giãn thì đây là bề rộng tối thiểu */
  width: number;
  grow?: boolean;
  /** Cột số: canh phải */
  numeric?: boolean;
  /** Cột chữ đứng sau cột số: lùi 14px như thiết kế */
  indent?: boolean;
  pin?: "left" | "right";
  /** Có thì bấm tiêu đề để sắp xếp theo giá trị này (bảng cũ mantine-react-table sắp xếp được) */
  sortValue?: (line: SaleOrderLineView) => string | number | null | undefined;
  cell: (line: SaleOrderLineView, index: number, ctx: CellContext) => React.ReactNode;
  total?: (totals: Totals) => React.ReactNode;
}

type SortState = { id: string; dir: "asc" | "desc" } | null;

/** Nội dung đang bị che dưới cột ghim trái / phải (để hiện bóng mép cột ghim) */
interface PinEdges {
  left: boolean;
  right: boolean;
}


const C = {
  surface: "#FFFFFF",
  headBg: "#F6F7F4",
  divider: "#ECEEE9",
  rowLine: "#F0F1EE",
  text: "#1B1F24",
  textSub: "#5C6570",
  muted: "#8A929B",
  empty: "#A3AAB2",
  accent: "#1F5FAE",
  highlight: "#E6EEF8",
  success: "#1E6B46",
  varianceRow: "#FFFBF3",
  danger: "#B42318",
  totalBg: "#FAFBF8",
};

const HEADER_HEIGHT = 34;
const ROW_HEIGHT = 36;
const TOTAL_HEIGHT = 40;
const EMPTY_HEIGHT = 48;
const HIGHLIGHT_MS = 1500;
/** Lề trái / phải của bảng; nằm trong cột ghim để khi cuộn ngang không lộ khe */
const EDGE_PADDING = 14;
const PIN_SHADOW_LEFT = "6px 0 6px -6px rgba(27, 31, 36, 0.28)";
const PIN_SHADOW_RIGHT = "-6px 0 6px -6px rgba(27, 31, 36, 0.28)";

/** Định dạng vi-VN (1.234.567); số âm dùng dấu trừ U+2212 */
const formatNumber = (value: number) => (value < 0 ? `−${formatNumberVi(-value)}` : formatNumberVi(value));

const lineTotal = (line: SaleOrderLineView) => (Number(line.soPrice1) || 0) * (line.primaryQuantity || 0);
const deviationOf = (line: SaleOrderLineView) => line.scannedQuantity - (line.primaryQuantity || 0);
const deviationColor = (value: number) => (value === 0 ? C.success : C.danger);

const copyButtonStyle: React.CSSProperties = {
  display: "inline-block",
  maxWidth: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  textAlign: "inherit",
  verticalAlign: "middle",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "copy",
};

const itemCodeCopyStyle: React.CSSProperties = { ...copyButtonStyle, color: C.accent, fontWeight: 700 };
const itemCodePickStyle: React.CSSProperties = { ...itemCodeCopyStyle, cursor: "pointer" };
const serialStyle: React.CSSProperties = { fontWeight: 500 };

const EmptyValue = () => <span style={{ color: C.empty }}>—</span>;

interface CopyTextProps {
  value: string;
  children: React.ReactNode;
  /** Tooltip khi rê chuột (tên dài bị cắt); không có thì chỉ báo "Đã sao chép" sau khi bấm */
  hint?: string;
  style?: React.CSSProperties;
}

/** Bấm để sao chép giá trị ô, giữ click-to-copy của bảng cũ (mantine-react-table) */
const CopyText = ({ value, children, hint, style }: CopyTextProps) => (
  <CopyButton value={value}>
    {({ copied, copy }) => (
      <Tooltip
        label={copied || !hint ? "Đã sao chép" : hint}
        color={copied || !hint ? C.success : undefined}
        openDelay={1000}
        opened={hint ? undefined : copied}
        multiline
        maw={360}
      >
        <button type="button" onClick={copy} style={style ? { ...copyButtonStyle, ...style } : copyButtonStyle}>
          {children}
        </button>
      </Tooltip>
    )}
  </CopyButton>
);

const textCell = (value: string | null | undefined, style?: React.CSSProperties) =>
  value ? (
    <CopyText value={value} style={style}>
      {value}
    </CopyText>
  ) : (
    <EmptyValue />
  );

const numberCell = (value: number | null | undefined) =>
  value == null ? <EmptyValue /> : <CopyText value={String(value)}>{formatNumber(Number(value))}</CopyText>;

interface ItemCodeButtonProps {
  itemCode: string;
  readOnly: boolean;
  onPickItem: (itemCode: string) => void;
}

/** Mã hàng: bấm để tìm serial của mã đó rồi cuộn lên đầu trang; mã vẫn được sao chép như bảng cũ. Chỉ xem: chỉ sao chép */
const ItemCodeButton = ({ itemCode, readOnly, onPickItem }: ItemCodeButtonProps) => (
  <CopyButton value={itemCode}>
    {({ copied, copy }) => {
      const hint = readOnly ? undefined : "Tìm serial";
      return (
        <Tooltip
          label={copied || !hint ? "Đã sao chép" : hint}
          color={copied || !hint ? C.success : undefined}
          openDelay={1000}
          opened={hint ? undefined : copied}
        >
          <button
            type="button"
            style={readOnly ? itemCodeCopyStyle : itemCodePickStyle}
            onClick={() => {
              copy();
              if (readOnly) return;
              onPickItem(itemCode);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            {itemCode}
          </button>
        </Tooltip>
      );
    }}
  </CopyButton>
);

// Thứ tự cột theo thiết kế; cột Serial (thiết kế không có) đặt sau "Đã quét". STT và Lệch ghim hai mép.
// SL2, ĐVT2 đã bỏ theo yêu cầu.
const COLUMNS: Column[] = [
  {
    id: "index",
    label: "STT",
    width: 40 + EDGE_PADDING,
    pin: "left",
    cell: (_line, index) => <span style={{ color: C.muted }}>{index + 1}</span>,
  },
  {
    id: "itemCode",
    label: "Mã hàng",
    width: 120,
    sortValue: (line) => line.itemCode,
    cell: (line, _index, ctx) => (
      <ItemCodeButton itemCode={line.itemCode} readOnly={ctx.readOnly} onPickItem={ctx.onPickItem} />
    ),
    total: () => <span style={{ color: C.danger }}>Tổng:</span>,
  },
  {
    id: "itemName",
    label: "Tên sản phẩm",
    sortValue: (line) => line.itemName,
    // 240px: ở bề rộng thiết kế (khối 916px) các cột thiết kế vẫn vừa khung khi có thanh cuộn dọc
    width: 240,
    grow: true,
    cell: (line) =>
      line.itemName ? (
        <CopyText value={line.itemName} hint={line.itemName}>
          {line.itemName}
        </CopyText>
      ) : (
        <EmptyValue />
      ),
  },
  {
    id: "primaryUom",
    label: "ĐVT",
    width: 56,
    sortValue: (line) => line.primaryUom,
    cell: (line) => textCell(line.primaryUom),
  },
  {
    id: "primaryQuantity",
    label: "SL",
    sortValue: (line) => line.primaryQuantity,
    width: 56,
    numeric: true,
    cell: (line) => numberCell(line.primaryQuantity),
    total: (totals) => formatNumber(totals.quantity),
  },
  {
    id: "soPrice1",
    label: "Giá bán",
    sortValue: (line) => Number(line.soPrice1) || 0,
    width: 104,
    numeric: true,
    cell: (line) => numberCell(Number(line.soPrice1) || 0),
  },
  {
    id: "totalAmount",
    label: "Thành tiền",
    sortValue: lineTotal,
    width: 112,
    numeric: true,
    cell: (line) => numberCell(lineTotal(line)),
    total: (totals) => <span style={{ color: C.danger }}>{formatNumber(totals.amount)}</span>,
  },
  {
    id: "scannedQuantity",
    label: "Đã quét",
    sortValue: (line) => line.scannedQuantity,
    width: 72,
    numeric: true,
    cell: (line) => numberCell(line.scannedQuantity),
    total: (totals) => formatNumber(totals.scanned),
  },
  {
    id: "serial",
    label: "Serial",
    // Chữ đứng sau cột số "Đã quét": lùi vào như thiết kế
    width: 120 + EDGE_PADDING,
    indent: true,
    sortValue: (line) => line.serial,
    cell: (line) => textCell(line.serial, serialStyle),
  },
  {
    id: "deviated",
    label: "Lệch",
    sortValue: deviationOf,
    width: 64 + EDGE_PADDING,
    numeric: true,
    pin: "right",
    cell: (line) => {
      const deviated = deviationOf(line);
      return <span style={{ color: deviationColor(deviated), fontWeight: 700 }}>{formatNumber(deviated)}</span>;
    },
    total: (totals) => <span style={{ color: deviationColor(totals.deviated) }}>{formatNumber(totals.deviated)}</span>,
  },
];

const GRID_TEMPLATE = COLUMNS.map((column) =>
  column.grow ? `minmax(${column.width}px, 1fr)` : `${column.width}px`,
).join(" ");
/** Hẹp hơn mức này thì cuộn ngang */
const MIN_TABLE_WIDTH = COLUMNS.reduce((sum, column) => sum + column.width, 0);

const rowBase: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: GRID_TEMPLATE,
  alignItems: "center",
  flexShrink: 0,
};

const headerRowStyle: React.CSSProperties = {
  ...rowBase,
  height: HEADER_HEIGHT,
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: C.headBg,
  color: C.textSub,
  fontSize: 12,
  fontWeight: 600,
};

// Hàng Tổng nằm đáy khung khi ít dòng (marginTop auto) và dính đáy khi cuộn
const totalRowStyle: React.CSSProperties = {
  ...rowBase,
  height: TOTAL_HEIGHT,
  marginTop: "auto",
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  borderTop: `1px solid ${C.divider}`,
  background: C.totalBg,
  fontSize: 13,
  fontWeight: 700,
};

const cellStyle = (column: Column, edges: PinEdges): React.CSSProperties => {
  const style: React.CSSProperties = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: column.numeric ? "right" : "left",
    paddingLeft: column.pin === "left" || column.indent ? EDGE_PADDING : column.numeric ? 8 : 0,
    paddingRight: column.pin === "right" ? EDGE_PADDING : column.numeric ? 0 : 8,
  };
  if (!column.pin) return style;
  let boxShadow: string | undefined;
  if (column.pin === "left" && edges.left) boxShadow = PIN_SHADOW_LEFT;
  if (column.pin === "right" && edges.right) boxShadow = PIN_SHADOW_RIGHT;
  // Cột ghim phủ hết chiều cao dòng, nền lấy theo dòng để che nội dung cuộn bên dưới
  return {
    ...style,
    position: "sticky",
    left: column.pin === "left" ? 0 : undefined,
    right: column.pin === "right" ? 0 : undefined,
    zIndex: 1,
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    justifyContent: column.numeric ? "flex-end" : "flex-start",
    background: "inherit",
    boxShadow,
  };
};

/** So sánh cho sắp xếp: số theo giá trị, chữ theo tiếng Việt; ô trống luôn nằm cuối */
const compareValues = (a: string | number | null | undefined, b: string | number | null | undefined) => {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "vi", { numeric: true, sensitivity: "base" });
};

const sortButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  maxWidth: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
};

const SortIcon = ({ dir }: { dir: "asc" | "desc" | null }) => (
  <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    <path d="M5 1L8.5 5h-7z" fill={dir === "asc" ? C.accent : C.empty} />
    <path d="M5 11L1.5 7h7z" fill={dir === "desc" ? C.accent : C.empty} />
  </svg>
);

const ariaSortOf = (dir: "asc" | "desc" | null) => (dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none");

interface OrderLineRowProps {
  line: SaleOrderLineView;
  index: number;
  highlighted: boolean;
  cellStyles: React.CSSProperties[];
  ctx: CellContext;
}

/** Một dòng đơn. Memo: mỗi lần quét chỉ dòng đổi số đã quét (object view mới) vẽ lại */
const OrderLineRowBase = ({ line, index, highlighted, cellStyles, ctx }: OrderLineRowProps) => {
  const deviated = deviationOf(line);
  let background = C.surface;
  if (highlighted) background = C.highlight;
  else if (deviated !== 0) background = C.varianceRow;
  return (
    <div
      role="row"
      data-item-code={line.itemCode}
      style={{
        ...rowBase,
        height: ROW_HEIGHT,
        borderBottom: `1px solid ${C.rowLine}`,
        background,
        transition: "background-color 0.3s ease",
      }}
    >
      {COLUMNS.map((column, columnIndex) => (
        <span key={column.id} role="cell" style={cellStyles[columnIndex]}>
          {column.cell(line, index, ctx)}
        </span>
      ))}
    </div>
  );
};
const OrderLineRow = React.memo(OrderLineRowBase);

/** Dòng đơn cần xuất, số đã quét và lệch. Giá dùng giá bán cho cả xuất bán và đổi trả */
const OrderLinesTable = ({ lines, height, readOnly, onPickItem, focusItemCode = null }: OrderLinesTableProps) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState<PinEdges>({ left: false, right: false });
  const [viewWidth, setViewWidth] = React.useState(0);
  const [highlightCode, setHighlightCode] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortState>(null);

  // STT giữ theo thứ tự gốc của đơn như bảng cũ (row.index); sắp xếp chỉ đổi thứ tự hiển thị
  const displayLines = React.useMemo(() => {
    const indexed = lines.map((line, index) => ({ line, index }));
    const column = sort ? COLUMNS.find((c) => c.id === sort.id) : undefined;
    if (!sort || !column?.sortValue) return indexed;
    const getValue = column.sortValue;
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...indexed].sort((a, b) => {
      const result = compareValues(getValue(a.line), getValue(b.line));
      return result === 0 ? a.index - b.index : result * direction;
    });
  }, [lines, sort]);

  // Bấm lần lượt: tăng dần, giảm dần, bỏ sắp xếp
  const toggleSort = React.useCallback((id: string) => {
    setSort((prev) => {
      if (!prev || prev.id !== id) return { id, dir: "asc" };
      return prev.dir === "asc" ? { id, dir: "desc" } : null;
    });
  }, []);

  const measure = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const left = element.scrollLeft > 0;
    const right = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    setViewWidth(element.clientWidth);
  }, []);

  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!focusItemCode || !container) return;
    const row = Array.from(container.querySelectorAll<HTMLElement>("[data-item-code]")).find(
      (element) => element.dataset.itemCode === focusItemCode,
    );
    if (!row) return;
    // Cuộn trong bảng để dòng nằm giữa hàng tiêu đề và hàng Tổng (hai hàng này dính), rồi đưa bảng vào tầm nhìn
    const top = row.offsetTop - HEADER_HEIGHT;
    const bottom = row.offsetTop + ROW_HEIGHT + TOTAL_HEIGHT - container.clientHeight;
    if (container.scrollTop > top) container.scrollTop = top;
    else if (container.scrollTop < bottom) container.scrollTop = bottom;
    container.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setHighlightCode(focusItemCode);
  }, [focusItemCode]);

  // Hẹn giờ theo dòng đang tô, không theo focusItemCode, để cha đặt lại null ngay cũng không làm kẹt màu
  React.useEffect(() => {
    if (!highlightCode) return undefined;
    const timer = window.setTimeout(() => setHighlightCode(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightCode]);

  const cellStyles = React.useMemo(() => COLUMNS.map((column) => cellStyle(column, edges)), [edges]);

  const totals = React.useMemo<Totals>(
    () =>
      lines.reduce<Totals>(
        (sum, line) => ({
          quantity: sum.quantity + (Number(line.primaryQuantity) || 0),
          amount: sum.amount + lineTotal(line),
          scanned: sum.scanned + line.scannedQuantity,
          deviated: sum.deviated + deviationOf(line),
        }),
        { quantity: 0, amount: 0, scanned: 0, deviated: 0 },
      ),
    [lines],
  );

  const ctx = React.useMemo<CellContext>(() => ({ readOnly, onPickItem }), [readOnly, onPickItem]);

  return (
    <div
      ref={scrollRef}
      onScroll={measure}
      style={{
        position: "relative",
        // Giữ z-index của hàng/cột dính bên trong bảng, không đè lên phần khác của trang
        isolation: "isolate",
        // Cha là khối flex cột có chiều cao cố định thì lấp đầy phần còn lại; không thì cao như bản trước
        flex: "1 1 auto",
        height: Math.max(0, height * 0.67),
        minHeight: 0,
        overflow: "auto",
        background: C.surface,
        color: C.text,
        fontSize: 12.5,
        lineHeight: "normal",
      }}
    >
      <div
        role="table"
        aria-label="Dòng đơn hàng"
        style={{ display: "flex", flexDirection: "column", minWidth: MIN_TABLE_WIDTH, minHeight: "100%" }}
      >
        <div role="row" style={headerRowStyle}>
          {COLUMNS.map((column, columnIndex) => {
            const dir = sort?.id === column.id ? sort.dir : null;
            return (
              <span
                key={column.id}
                role="columnheader"
                aria-sort={column.sortValue ? ariaSortOf(dir) : undefined}
                style={cellStyles[columnIndex]}
              >
                {column.sortValue ? (
                  <button type="button" style={sortButtonStyle} onClick={() => toggleSort(column.id)}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{column.label}</span>
                    <SortIcon dir={dir} />
                  </button>
                ) : (
                  column.label
                )}
              </span>
            );
          })}
        </div>

        {lines.length === 0 ? (
          <div
            role="row"
            style={{ display: "flex", flexShrink: 0, height: EMPTY_HEIGHT, borderBottom: `1px solid ${C.rowLine}` }}
          >
            <span
              role="cell"
              aria-colspan={COLUMNS.length}
              style={{
                position: "sticky",
                left: 0,
                width: viewWidth || "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: C.textSub,
              }}
            >
              Không có dòng đơn
            </span>
          </div>
        ) : (
          displayLines.map(({ line, index }) => (
            <OrderLineRow
              key={line.itemCode}
              line={line}
              index={index}
              highlighted={highlightCode === line.itemCode}
              cellStyles={cellStyles}
              ctx={ctx}
            />
          ))
        )}

        <div role="row" style={totalRowStyle}>
          {COLUMNS.map((column, columnIndex) => (
            <span key={column.id} role="cell" style={cellStyles[columnIndex]}>
              {column.total?.(totals)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(OrderLinesTable);
